import React, { useState, useContext, useEffect, useRef, useCallback } from "react";
import {
  Text,
  Button,
  Group as MantineGroup,
  Stack,
  Box,
  useMantineTheme,
  Loader,
  Alert,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle, IconX } from "@tabler/icons-react";
import FormField from "./form-field";
import { DataContext } from "../context/DataContext";
import useStatusCheck from "../hooks/useStatusCheck";
import useEdgeStatusCheck from "../hooks/useEdgeStatusCheck";
import useLocale from "../hooks/useLocale";
import VerificationProcessingScreen from "./verification-processing-screen";
import useAdjustInputSize from "../hooks/useAdjustInputSize";
import { normalizeDob } from "../utils/validateDob";
import { getEdgeQueueUrl, getEdgeRetrieveUrl } from "../utils/edge";

// BasicDataCollectionForm is the generic single-page form that
// services multiple componentKeys (idmatch/plus, idmatch/coppa,
// agematch/process, etc.). The edge queue dispatches by `queue`
// name on the request body, so route the request to the matching
// worker — sending coppa traffic to the idmatchplus queue would
// hit the wrong backend handler and skew the response shape we
// then read in handleStatusResult.
const queueForComponent = (componentKey) => {
  if (componentKey === "idmatch/coppa") return "coppa";
  if (componentKey === "agematch/process") return "agematch";
  return "idmatchplus";
};

const BasicDataCollectionForm = ({
  title,
  subtitle,
  icon,
  componentKey,
  endpoint,
  config,
  buttonText,
  loadingText,
  errorText,
  reAttempts,
  onApiResponse,
  // The initial-load effect auto-submits whenever the prefilled values
  // already validate (prefill flows). Review-style datamatch flows set
  // this to keep the populated form on screen for confirmation instead.
  // NOTE: that built-in path only POSTs in edge mode — non-edge just
  // starts status polling (it assumes a prior step already submitted).
  skipInitialAutoSubmit = false,
  // Submit through submitHandler() on mount — a real submission in
  // BOTH edge and non-edge modes. Datamatch flows with review disabled
  // use this (together with skipInitialAutoSubmit to avoid a double
  // edge submission). Validation failures fall back to showing the
  // populated form.
  autoSubmit = false,
  // Render every field disabled — review-without-edit datamatch mode.
  readOnly = false,
  // Called instead of dispatching a terminal FAIL when the verification
  // fails with no attempts left. Lets the parent route the user into a
  // fallback step (e.g. blinkid datamatch → document verification)
  // rather than ending the journey.
  onTerminalFail = null,
  // A retryable failure (backend WAITING / FAIL with attempts left)
  // normally re-surfaces the form for the user to correct their data.
  // Flows where the user is never allowed to enter data (blinkid
  // datamatch with review or edit disabled) must not show the
  // data-collection screen at all — the payload came off the barcode
  // and can't change, so a retry is the same request again. With this
  // set, any retry signal finalizes as FAIL instead (which routes into
  // onTerminalFail / journey FAIL like an exhausted-attempts failure).
  suppressRetryForm = false,
}) => {
  const { data, text, dispatch, settings } = useContext(DataContext);
  // Retry alert strings come from the coppa locale — they're the
  // same conceptual message regardless of which data-only flow is
  // running, and they're already translated into all 44 languages.
  const retryLocale = useLocale("coppa");
  const theme = useMantineTheme();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(false);
  const [attemptsLeft, setAttemptsLeft] = useState(reAttempts);
  const [statusResponse, setStatusResponse] = useState(null);
  // Start in the verifying state when auto-submitting so the processing
  // screen shows from the first frame instead of the form flashing.
  const [isVerifying, setIsVerifying] = useState(autoSubmit);
  // Terminal status captured from polling. The processing screen
  // holds "Completing verification…" until this is non-null, then
  // fires onComplete which dispatches navigation.
  const [terminalStatus, setTerminalStatus] = useState(null);
  // Flipped true whenever the backend returns a non-PASS while the
  // user still has attempts left. Drives the inline retry Alert
  // rendered above the form. The user can dismiss the Alert (sets
  // this back to false), and a fresh failure on the next attempt
  // flips it back to true so the Alert re-appears.
  const [hasReceivedFailedResponse, setHasReceivedFailedResponse] = useState(false);
  const inputSize = useAdjustInputSize();
  const lastConfirmationRef = useRef(null);
  const confirmationsReceivedRef = useRef([]);
  const totalAttemptsUsedRef = useRef(0);
  const waitingForConfirmationChangeRef = useRef(false);
  const initialLoadRef = useRef(true);
  const [currentAttempt, setCurrentAttempt] = useState(0);
  const [hasShownFormForCurrentAttempt, setHasShownFormForCurrentAttempt] = useState(false);
  const [edgeStatusPolling, setEdgeStatusPolling] = useState(false);

  const buttonContentColor =
    data.styles?.components?.button?.color ||
    (theme.colorScheme === "dark" ? theme.white : theme.black);

  // Check if edge mode is enabled
  const isEdgeMode = data?.edge === true;

  // Status check handler
  const handleStatusResult = (statusResult, confirmationChanged) => {
    if (initialLoadRef.current && statusResult) {
      initialLoadRef.current = false;
    }

    setStatusResponse(statusResult);
    const confirmationNumber = statusResult.confirmation || "unknown";

    if (statusResult.process_state === "NEXT_APP_TRIGGER") {
      setTerminalStatus("NEXT_APP");
      setLoading(true);
      return false;
    }

    // Terminal first — the backend can return process_state="WAITING"
    // alongside is_final:true / attempts_remaining:0 when the final
    // attempt didn't match, so we have to detect "this is the end"
    // BEFORE we interpret WAITING as a retry signal. Treat any of
    // these as terminal:
    //   • process_state === "COMPLETED"
    //   • is_final === true
    //   • attempts_remaining === 0 (no retries left)
    // For PASS vs FAIL, prefer the top-level `status` field, but
    // fall back to `latest_event.status` because in some terminal
    // responses the top-level status stays "PENDING" and only the
    // latest_event carries the real PASS/FAIL.
    const isTerminal =
      statusResult.process_state === "COMPLETED" ||
      statusResult.is_final === true ||
      statusResult.attempts_remaining === 0;
    if (isTerminal) {
      const topStatus = statusResult.status;
      const eventStatus = statusResult.latest_event?.status;
      const finalStatus =
        topStatus === "PASS" || topStatus === "FAIL"
          ? topStatus
          : eventStatus === "PASS"
            ? "PASS"
            : "FAIL";
      setTerminalStatus(finalStatus);
      setLoading(true);
      setIsVerifying(false);
      return false;
    }

    // Retry signal: process_state="WAITING" only counts as retry
    // when there are attempts left (the terminal check above
    // already caught attempts_remaining === 0).
    if (statusResult.process_state === "WAITING") {
      if (suppressRetryForm) {
        setTerminalStatus("FAIL");
        setIsVerifying(false);
        setLoading(true);
        return false;
      }
      setHasShownFormForCurrentAttempt(true);
      setIsVerifying(false);
      setLoading(false);
      setResult(false);
      setEdgeStatusPolling(false);
      setHasReceivedFailedResponse(true);
      try {
        dispatch({ type: "SET_STATUS", payload: "PENDING" });
        dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: false });
      } catch (err) {
        // Silent fail
      }
      return false;
    }

    if (statusResult.status === "PASS") {
      setTerminalStatus("PASS");
      setLoading(true);
      return false;
    }

    // Every branch below re-surfaces the form for another attempt —
    // off-limits when the user isn't allowed to enter data.
    if (suppressRetryForm && statusResult.status === "FAIL") {
      setTerminalStatus("FAIL");
      setIsVerifying(false);
      setLoading(true);
      return false;
    }

    if (
      statusResult.status === "FAIL" &&
      !hasShownFormForCurrentAttempt &&
      initialLoadRef.current
    ) {
      initialLoadRef.current = false;
      setCurrentAttempt(1);
      setHasShownFormForCurrentAttempt(true);
      setAttemptsLeft(reAttempts);
      setLoading(false);
      setResult(false);
      setEdgeStatusPolling(false);
      setHasReceivedFailedResponse(true);
      return false;
    }

    if (
      (currentAttempt === 0 || currentAttempt === 1) &&
      statusResult.status === "FAIL" &&
      !hasShownFormForCurrentAttempt
    ) {
      lastConfirmationRef.current = confirmationNumber;
      if (currentAttempt === 0) {
        setCurrentAttempt(1);
      }
      setHasShownFormForCurrentAttempt(true);
      setAttemptsLeft(reAttempts);
      setLoading(false);
      setResult(false);
      setEdgeStatusPolling(false);
      setHasReceivedFailedResponse(true);
      return false;
    }

    if (waitingForConfirmationChangeRef.current && confirmationChanged) {
      waitingForConfirmationChangeRef.current = false;
      lastConfirmationRef.current = confirmationNumber;
      confirmationsReceivedRef.current.push(confirmationNumber);
      setHasShownFormForCurrentAttempt(false);

      if (statusResult.status !== "PASS" && attemptsLeft > 0) {
        if (suppressRetryForm) {
          setTerminalStatus("FAIL");
          setIsVerifying(false);
          setLoading(true);
          return false;
        }
        setLoading(false);
        setResult(false);
        setEdgeStatusPolling(false);
        setHasReceivedFailedResponse(true);
        return false;
      }

      if (statusResult.status !== "PASS" && attemptsLeft === 0) {
        setTerminalStatus("FAIL");
        setLoading(true);
        return false;
      }

      setLoading(true);
      return true;
    }

    try {
      dispatch({ type: "SET_STATUS", payload: "PENDING" });
      dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: false });
    } catch (err) {
      // Silent fail
    }

    setLoading(true);

    if (!result) {
      setResult(true);
    }

    return true;
  };

  const handleProcessingComplete = useCallback(() => {
    if (terminalStatus === "NEXT_APP") {
      dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
      dispatch({ type: "TRIGGER_NAVIGATION" });
      return;
    }
    const finalStatus = terminalStatus || "FAIL";
    // Terminal FAIL with a fallback handler: hand control back to the
    // parent (which swaps in the fallback verification) instead of
    // marking the journey failed. Status stays PENDING so the fallback
    // step's own submission drives the eventual PASS/FAIL.
    if (finalStatus === "FAIL" && typeof onTerminalFail === "function") {
      onTerminalFail();
      return;
    }
    dispatch({ type: "SET_STATUS", payload: finalStatus });
    dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
  }, [terminalStatus, dispatch, onTerminalFail]);

  // Status check hooks - use edge status check when in edge mode and polling is triggered
  useStatusCheck(!isEdgeMode && result, false, componentKey, handleStatusResult);
  useEdgeStatusCheck(isEdgeMode && edgeStatusPolling, handleStatusResult);

  // Form setup
  const form = useForm({
    initialValues: config.fields.reduce((acc, field) => {
      if (field.type === "address") {
        acc["addr"] = data.addr || "";
        acc["city"] = data.city || "";
        acc["state"] = data.state || "";
        acc["zip"] = data.zip || "";
      } else {
        acc[field.name] = data[field.name] || field.initialValue || "";
      }
      return acc;
    }, {}),
    validateInputOnChange: true,
    validateInputOnBlur: true,
    validate: (values) => {
      const errors = {};
      config.fields.forEach((field) => {
        if (field.validation) {
          let error;
          if (field.name === "address" && field.type === "address") {
            error = field.validation({
              addr: values.addr || "",
              city: values.city || "",
              state: values.state || "",
              zip: values.zip || "",
            });
          } else {
            error = field.validation(values[field.name]);
          }
          if (error) {
            if (field.type === "address") {
              errors.addr = error;
            } else {
              errors[field.name] = error;
            }
          }
        }
      });
      return errors;
    },
  });

  // Submit handler
  const submitHandler = async () => {
    setIsVerifying(true);
    const errors = form.validate();

    if (errors.hasErrors) {
      // With the form suppressed the fields are locked, so dropping the
      // user onto it to fix a validation error would strand them —
      // finalize as FAIL (→ onTerminalFail / journey FAIL) instead.
      if (suppressRetryForm) {
        setTerminalStatus("FAIL");
        setIsVerifying(false);
        setLoading(true);
        return;
      }
      setIsVerifying(false);
      return;
    }

    const newAttemptsLeft = attemptsLeft - 1;
    setAttemptsLeft(newAttemptsLeft);

    Object.keys(form.values).forEach((key) => {
      data[key] = form.values[key];
    });

    if (!data.settings) data.settings = {};
    data.settings.currentAttempt = currentAttempt;
    data.settings.attemptsLeft = newAttemptsLeft;
    data.attemptsLeft = newAttemptsLeft;

    try {
      let apiUrl;
      let requestData = { ...data };

      if (isEdgeMode) {
        const edgeQueueUrl = getEdgeQueueUrl();
        apiUrl = edgeQueueUrl;
        requestData.queue = queueForComponent(componentKey);
      } else {
        apiUrl = `${process.env.REACT_APP_BACKEND_URL}${endpoint}`;
      }

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      });

      const responseData = await response.json();

      if (isEdgeMode) {
        // In edge mode, the next step depends on the flow type:
        // idmatch/plus generates KBA challenge questions that need a
        // /retrieve poll; everything else (coppa, agematch, …) goes
        // straight to status polling — there are no follow-up
        // questions to fetch.
        const reqId = responseData.request_id || data.request_id;
        if (!reqId) {
          console.error("No request_id found in edge response");
          setIsVerifying(false);
        } else if (componentKey === "idmatch/plus") {
          pollForQuestions(reqId, onApiResponse);
        } else {
          setIsVerifying(false);
          setLoading(true);
          setEdgeStatusPolling(true);
        }
      } else {
        // Non-edge mode: handle synchronous response
        if (typeof onApiResponse === "function") {
          onApiResponse(responseData);
        }

        try {
          dispatch({ type: "SET_STATUS", payload: "PENDING" });
          dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: false });
        } catch (err) {
          // Silent fail
        }

        setIsVerifying(false);
        setLoading(true);

        setCurrentAttempt((prevAttempt) => prevAttempt + 1);
        waitingForConfirmationChangeRef.current = true;
        setResult(true);
      }
    } catch (error) {
      alert(errorText || "Something Went Wrong! Please try again.");
      setIsVerifying(false);
    }
  };

  // Auto-submit once on mount when requested (datamatch flows with
  // review disabled). Uses submitHandler so the payload actually POSTs
  // in both edge (queue) and non-edge (v3 endpoint) modes.
  const autoSubmittedRef = useRef(false);
  useEffect(() => {
    if (autoSubmit && !autoSubmittedRef.current) {
      autoSubmittedRef.current = true;
      submitHandler();
    }
  }, [autoSubmit]);

  // Polling function for edge mode to get questions
  const pollForQuestions = async (requestId, onApiResponse) => {
    const maxAttempts = 100; // 5 minutes max
    const pollInterval = 3000; // 3 seconds
    let attempts = 0;

    const poll = async () => {
      try {
        const edgeRetrieveUrl = getEdgeRetrieveUrl();

        const response = await fetch(edgeRetrieveUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            request_id: requestId,
            type: "idmatchplus"
          }),
        });

        if (response.ok) {
          const result = await response.json();

          // Check if we got a final result with no questions - trigger edge status polling.
          // This covers: explicit PASS/FAIL status, or a PENDING status with action FAIL
          // (identity didn't match, no questions generated).
          if (!result.has_questions && (result.status === "PASS" || result.status === "FAIL" || result.action === "FAIL")) {
            setIsVerifying(false);
            setLoading(true);
            setEdgeStatusPolling(true);
            return;
          }

          // Check if we have questions
          if (result.has_questions && result.questions && result.questions.length > 0) {
            // Transform edge response to match expected format for handleApiResponse
            const transformedResponse = {
              idr5_response: {
                output: {
                  questions: {
                    questions: result.questions
                  }
                },
                continuations: {
                  questions: {
                    template: result.continuation_template
                  }
                }
              }
            };

            if (typeof onApiResponse === "function") {
              onApiResponse(transformedResponse);
            }

            setIsVerifying(false);
            setCurrentAttempt((prevAttempt) => prevAttempt + 1);
            return; // Success, stop polling
          }
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          console.error("Polling timeout: No questions received after 5 minutes");
          setIsVerifying(false);
        }
      } catch (error) {
        console.error("Polling error:", error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          setIsVerifying(false);
        }
      }
    };

    poll();
  };

  const handleDobChange = (dob) => {
    const formattedDob = normalizeDob(`${dob.year || ""}${dob.month || ""}${dob.day || ""}`);
    form.setFieldValue("dob", formattedDob);
  };

  const handleAddressChange = (address) => {
    form.setFieldValue("addr", address.addr);
    form.setFieldValue("city", address.city);
    form.setFieldValue("state", address.state);
    form.setFieldValue("zip", address.zip);
  };

  // Clear validation errors when a retry attempt comes back —
  // the previous attempt's field-level errors aren't useful any
  // more, the inline Alert above the form carries the real signal.
  useEffect(() => {
    if (hasReceivedFailedResponse) {
      form.clearErrors();
    }
  }, [hasReceivedFailedResponse]);

  // Auto-dismiss the retry banner after 5 seconds. The countdown ring
  // in the banner UI mirrors this timer visually.
  useEffect(() => {
    if (!hasReceivedFailedResponse) return;
    const t = setTimeout(() => setHasReceivedFailedResponse(false), 5000);
    return () => clearTimeout(t);
  }, [hasReceivedFailedResponse]);

  // Initial load effect
  useEffect(() => {
    setCurrentAttempt(0);
    setHasShownFormForCurrentAttempt(false);
    setAttemptsLeft(reAttempts);

    const errors = form.validate();
    if (!errors.hasErrors && !skipInitialAutoSubmit) {
      if (!data.settings) data.settings = {};
      data.settings.currentAttempt = 0;
      data.settings.totalAttempts = reAttempts + 1;
      data.attemptsLeft = reAttempts;

      // For edge mode, we need to submit to the edge queue endpoint first
      if (isEdgeMode) {
        const autoSubmitEdge = async () => {
          setIsVerifying(true);
          setLoading(true);

          // Copy form values to data
          Object.keys(form.values).forEach((key) => {
            data[key] = form.values[key];
          });

          try {
            const edgeQueueUrl = getEdgeQueueUrl();

            const requestData = { ...data, queue: queueForComponent(componentKey) };

            const response = await fetch(edgeQueueUrl, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(requestData),
            });

            const responseData = await response.json();
            const reqId = responseData.request_id || data.request_id;

            // Same branching as the manual submit path — only
            // idmatch/plus has KBA questions to poll for.
            if (!reqId) {
              console.error("No request_id found in edge response");
              setIsVerifying(false);
              setLoading(false);
            } else if (componentKey === "idmatch/plus") {
              pollForQuestions(reqId, onApiResponse);
            } else {
              setIsVerifying(false);
              setLoading(true);
              setEdgeStatusPolling(true);
            }
          } catch (error) {
            console.error("Edge auto-submit error:", error);
            setIsVerifying(false);
            setLoading(false);
          }
        };

        autoSubmitEdge();
      } else {
        // Non-edge mode: start status polling
        setLoading(true);
        setResult(true);
      }
    } else {
      setLoading(false);
      form.clearErrors();
    }
  }, [componentKey]);

  // Render the processing screen as soon as the user submits
  // (isVerifying), not only once loading flips after the API
  // responds — validation failures flip isVerifying back
  // synchronously, so a bad submission doesn't flash the screen.
  if (loading || isVerifying) {
    return (
      <VerificationProcessingScreen
        type="datamatch"
        resultReady={terminalStatus !== null}
        onComplete={handleProcessingComplete}
      />
    );
  }

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      {hasReceivedFailedResponse && (
        // Floating retry alert — positioned fixed so it hovers over
        // the form without pushing fields down. High z-index keeps
        // it above the Mantine inputs and the OTP overlays. Top
        // offset clears the iframe's close-X header strip. The
        // bounce-in entrance ("back" easing with overshoot) draws
        // the user's eye so the error registers immediately.
        <Box
          sx={{
            position: "fixed",
            top: 70,
            left: 16,
            right: 16,
            zIndex: 1500,
            pointerEvents: "none",
            animation:
              "retryAlertBounceIn 550ms cubic-bezier(0.34, 1.56, 0.64, 1) both",
            "@keyframes retryAlertBounceIn": {
              "0%": { opacity: 0, transform: "translateY(-32px) scale(0.96)" },
              "60%": { opacity: 1, transform: "translateY(4px) scale(1.01)" },
              "100%": { opacity: 1, transform: "translateY(0) scale(1)" },
            },
          }}
        >
          <Alert
            icon={<IconAlertCircle size={18} />}
            title={retryLocale?.retry?.title || "Whoops! That did not go as expected"}
            color="red"
            variant="light"
            styles={(theme) => ({
              root: {
                pointerEvents: "auto",
                position: "relative",
                paddingRight: 48,
                backgroundColor: theme.colors.red[0],
                borderColor: theme.colors.red[2],
                boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
              },
              title: { color: theme.colors.red[9], fontWeight: 600 },
              message: { color: theme.colors.red[9] },
              icon: { color: theme.colors.red[7] },
            })}
          >
            {retryLocale?.retry?.message ||
              "Please check that you've entered your full legal name, and provided an accurate date of birth or SSN."}
            {/* Countdown ring sits where the close-X would normally
                live. The stroke depletes over 5s in sync with the
                auto-dismiss useEffect. Clicking it dismisses early. */}
            <Box
              role="button"
              aria-label={retryLocale?.sharedMessages?.dismiss || "Dismiss"}
              onClick={() => setHasReceivedFailedResponse(false)}
              sx={(t) => ({
                position: "absolute",
                top: 12,
                right: 12,
                width: 28,
                height: 28,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "@keyframes retryRingDeplete": {
                  "0%": { strokeDashoffset: 0 },
                  "100%": { strokeDashoffset: 75.4 },
                },
              })}
            >
              <svg
                width="28"
                height="28"
                viewBox="0 0 28 28"
                style={{ position: "absolute", inset: 0 }}
              >
                <circle
                  cx="14"
                  cy="14"
                  r="12"
                  fill="none"
                  stroke={theme.colors.red[2]}
                  strokeWidth="2"
                />
                <circle
                  cx="14"
                  cy="14"
                  r="12"
                  fill="none"
                  stroke={theme.colors.red[7]}
                  strokeWidth="2"
                  strokeLinecap="round"
                  transform="rotate(-90 14 14)"
                  style={{
                    strokeDasharray: 75.4,
                    strokeDashoffset: 0,
                    animation: "retryRingDeplete 5s linear forwards",
                  }}
                />
              </svg>
              <IconX
                size={14}
                color={theme.colors.red[9]}
                style={{ position: "relative" }}
              />
            </Box>
          </Alert>
        </Box>
      )}
      <form
        onSubmit={form.onSubmit(submitHandler)}
        style={{
          display: "flex",
          flexDirection: "column",
          animation: "fadeIn 0.6s ease-out",
          height: "100%",
          minHeight: 0,
        }}
      >
        <Box
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            width: "100%",
            minHeight: 0,
          }}
        >
          {/* Inputs scroll internally so the submit button below
              stays pinned and visible no matter how many fields the
              route asks for. `minHeight: 0` is required for the flex
              child to actually shrink and trigger overflow. */}
          <Box
            style={{
              flex: 1,
              width: "100%",
              minHeight: 0,
              overflowY: "auto",
              paddingBottom: 12,
            }}
          >
            <Stack spacing={6} mb="md" align="flex-start">
              <Text fz={inputSize === "md" ? 24 : 20} fw={700}>
                {title}
              </Text>
              <Text c="dimmed" fz={inputSize}>
                {subtitle}
              </Text>
            </Stack>
            <Stack spacing="sm">
              {config.fields.map((field) => (
                <FormField
                  key={field.name}
                  field={field}
                  form={form}
                  settings={settings}
                  componentKey={componentKey}
                  showMoreFields={true}
                  onDobChange={handleDobChange}
                  onAddressChange={handleAddressChange}
                  isSinglePageForm={true}
                  disabled={readOnly}
                />
              ))}
            </Stack>
          </Box>
          <Box mt="md" style={{ width: "100%", flexShrink: 0 }}>
            <Button
              fullWidth
              size="lg"
              type="submit"
              disabled={isVerifying}
              sx={{
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "scale(1.01)",
                },
                '&[data-loading="true"]': {
                  color: buttonContentColor,
                },
              }}
            >
              {isVerifying ? (
                <MantineGroup spacing="xs" noWrap>
                  <Loader size="xs" color={theme.primaryColor} />
                  <Text c="inherit">{loadingText || "Getting Questions..."}</Text>
                </MantineGroup>
              ) : (
                buttonText
              )}
            </Button>
          </Box>
        </Box>
      </form>
    </>
  );
};

export default BasicDataCollectionForm;
